import { type NewsItem } from '@/lib/supabase';

type Props = {
  item: NewsItem;
  onClose: () => void;
  onEdit: () => void;
};

export default function NewsPreviewModal({ item, onClose, onEdit }: Props) {
  const wordCount = item.content ? item.content.split(/\s+/).length : 0;
  const readMinutes = Math.max(1, Math.ceil(wordCount / 200));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-[#0A0E27]/5 rounded-lg">
              <i className="ri-eye-line text-[#0A0E27] text-base" />
            </div>
            <div>
              <h2 className="text-sm font-black text-[#0A0E27]">Haber Önizleme</h2>
              <p className="text-xs text-gray-400">Yayınlandığında bu şekilde görünecek</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onEdit}
              className="flex items-center gap-1.5 bg-[#0A0E27] text-white text-xs font-bold px-4 py-2 rounded-lg hover:bg-[#0A0E27]/80 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-edit-line" />Düzenle
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer text-gray-500"
            >
              <i className="ri-close-line text-base" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1">
          {/* Hero Image */}
          <div className="relative w-full h-64 md:h-80 flex-shrink-0">
            <img
              src={item.image}
              alt={item.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E27]/70 via-transparent to-transparent" />
            {item.featured && (
              <div className="absolute top-4 left-4 flex items-center gap-1.5 bg-yellow-400 text-yellow-900 text-xs font-black px-3 py-1.5 rounded-full">
                <i className="ri-star-fill" />Öne Çıkan
              </div>
            )}
            <div className="absolute bottom-4 left-4 right-4">
              <span className="inline-block text-xs font-bold bg-[#00D9FF]/20 text-[#00D9FF] border border-[#00D9FF]/30 px-2.5 py-1 rounded-full mb-2 capitalize">
                {item.category}
              </span>
              <h1 className="text-white font-black text-xl md:text-2xl leading-tight line-clamp-3">
                {item.title}
              </h1>
            </div>
          </div>

          {/* Meta Bar */}
          <div className="flex flex-wrap items-center gap-4 px-6 py-4 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <i className="ri-user-line text-gray-400" />
              <span className="font-semibold text-[#0A0E27]">{item.author}</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <i className="ri-calendar-line text-gray-400" />
              <span>{item.date}</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <i className="ri-time-line text-gray-400" />
              <span>~{readMinutes} dk okuma</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <i className="ri-eye-line text-gray-400" />
              <span>{(item.views || 0).toLocaleString()} görüntüleme</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <i className="ri-file-text-line text-gray-400" />
              <span>{wordCount} kelime</span>
            </div>
          </div>

          {/* Excerpt */}
          {item.excerpt && (
            <div className="px-6 pt-5 pb-2">
              <p className="text-base text-gray-600 leading-relaxed font-medium border-l-4 border-[#00D9FF] pl-4 italic">
                {item.excerpt}
              </p>
            </div>
          )}

          {/* Content */}
          <div className="px-6 py-5">
            {item.content ? (
              <div
                className="prose prose-sm max-w-none text-gray-700 leading-relaxed"
                style={{ fontSize: '15px', lineHeight: '1.8' }}
                dangerouslySetInnerHTML={{ __html: item.content.replace(/\n/g, '<br/>') }}
              />
            ) : (
              <div className="text-center py-10 text-gray-300">
                <i className="ri-file-text-line text-4xl block mb-2" />
                <p className="text-sm">İçerik henüz eklenmemiş</p>
              </div>
            )}
          </div>

          {/* Tags */}
          {item.tags && item.tags.length > 0 && (
            <div className="px-6 pb-5 flex flex-wrap gap-2">
              {item.tags.map((tag: string) => (
                <span
                  key={tag}
                  className="text-xs bg-gray-100 text-gray-500 px-3 py-1 rounded-full font-medium"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Footer Info */}
          <div className="mx-6 mb-6 p-4 bg-[#0A0E27]/3 rounded-xl border border-gray-100">
            <div className="flex flex-wrap items-center gap-4 text-xs text-gray-400">
              <span className="flex items-center gap-1">
                <i className="ri-link-m" />
                <span className="font-mono text-[11px] truncate max-w-xs">/haber/{item.id}</span>
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-database-2-line" />
                ID: <span className="font-mono text-[11px]">{item.id.slice(0, 8)}...</span>
              </span>
              {item.featured && (
                <span className="flex items-center gap-1 text-yellow-600">
                  <i className="ri-star-fill" />Öne çıkan haber
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
